// @ts-strict-ignore
import { ICreateArticleOperation } from './i-create-article-operation';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { IOperation } from '@shared/undo/operation/i-operation';
import { Dictionary } from 'typescript-collections';
import { Vector3 } from 'three';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ProjectModel } from '@project/model/project.model';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { InsertArticlePrepareData } from './insert/insert-article-prepare-data';
import { RelativePositionReferenceArticleLocationVO } from '../../model/vo/relative-position-reference-article-location.vo';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ArticleUsagePlausiPropertyDef } from '@article/constants/article-usage-plausi-property-def';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { OnRailReferenceArticleLocationVO } from '../../model/vo/on-rail-reference-article-location.vo';
import { OpenLabelEditorHelper } from '@project/components/label/label-editor/service/open-label-editor.helper';
import { ReplaceReferenceArticleLocationVO } from '@article/model/vo/replace-reference-article-location.vo';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { InsertArticleUtils } from '../../util/insert-article-utils';
import { InsertArticleService } from '../../service/insert-article.service';
import { InsertedArticleData } from '../i-article-location.processor';

export class InsertArticleOperation implements ICreateArticleOperation {
  private static readonly INSERT_ON_RAIL_MESSAGE: string = 'insertAccessoryOnRail';
  private static readonly INSERT_BY_NEIGHBOR_MESSAGE: string = 'insertAccessoryByNeighbor';

  private _insertHelper: InsertArticleHelper;

  private _articleToCreate: ArticleVO;

  private _processingInsertData: InsertArticlePrepareData;

  private _createdUndoOperations: IOperation[] = [];
  private _createdContainers: ArticleContainer[] = [];
  private _moveArticlesMap: Dictionary<string, Vector3> = null;

  private _failureMessage: any;
  private _articleInserted: boolean = false;

  private _usageIdsToReplace: string[] = [];
  private _replacedUsageIds: string[] = [];

  constructor(
    private projectModel: ProjectModel,
    private openLabelEditorHelper: OpenLabelEditorHelper,
    private containerService: ContainerService,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private _insertArticleService: InsertArticleService
  ) { }

  public set usageIdsToReplace(usageIds: string[]) {
    this._usageIdsToReplace = usageIds || [];
  }

  public get usageIdsToDetach(): string[] {
    return null;
  }

  public get moveArticlesMap(): Dictionary<string, Vector3> {
    return this._moveArticlesMap;
  }

  public get createdArticleContainers(): ArticleContainer[] {
    return this._createdContainers;
  }

  public get createdUndoOperations(): IOperation[] {
    return this._createdUndoOperations;
  }

  public async execute(articleToCreate: ArticleVO, insertPrepareDatas: InsertArticlePrepareData[]): Promise<{removeSelection: boolean}> {
    this._articleToCreate = articleToCreate;

    for (let insertData of insertPrepareDatas) {
      this._processingInsertData = insertData;
      await this.processInsertData()
    }
    return this.finalizeInsertOperation();
  }

  private async processInsertData(): Promise<void> {
    this._insertHelper = this.insertArticleHelperFactory.createInstanceByAcceptingIdentity(this._processingInsertData.acceptingIdentityToInsert);
    const isNeighborArticle = this._processingInsertData.neighborArticleUsageIds && this._processingInsertData.neighborArticleUsageIds.length > 0

    if (isNeighborArticle) {
      const insertedNeighbors = await this.insertArticleByNeighbors()
        .catch((error: Error) => this.insertAccessoryByNeighbor_failure(error.message));

      if (insertedNeighbors) {
        await this.articleByNeighborsInserted(insertedNeighbors)
      } else {
        this.insertAccessoryByNeighbor_failure("neighbor article to insert could not be loaded")
      }
      return;
    }

    const insertedData = await this.insertArticleOnRail()
      .catch((error: Error) => this.insertAccessoryOnRail_failure(error.message));

    if (insertedData) {
      this.articleOnRailInserted(insertedData);
    } else {
      this.insertAccessoryOnRail_failure("article to insert could not be loaded");
    }
  }

  private insertArticleOnRail(): Promise<InsertedArticleData> {
    const connectedArticles: ArticleContainer[] = this._insertHelper.getAllConnectedArticles(true);
    const selectedArticles: ArticleContainer[] = [];
    for (const connectedContainer of connectedArticles) {
      if (connectedContainer.articleSelected) {
        selectedArticles.push(connectedContainer);
      }
    }

    let lastConnectedContainer: ArticleContainer = null;
    if (connectedArticles.length > 0) {
      lastConnectedContainer =
        selectedArticles.length > 0 ? selectedArticles[selectedArticles.length - 1] : connectedArticles[connectedArticles.length - 1];
    }

    const usageIdToReplace: string = connectedArticles.map((a) => a.articleGUID).find((g) => this._usageIdsToReplace.indexOf(g) >= 0);
    if (usageIdToReplace) {
      return this.replaceByUsageId(usageIdToReplace, InsertArticleOperation.INSERT_ON_RAIL_MESSAGE);
    } else if (!lastConnectedContainer) {
      return this.insertByOffset(this._processingInsertData.acceptingIdentityToInsert.articleUsageId,
        this._insertHelper.getConfiguredStartOffset(), this._processingInsertData.rotationDelta,
        InsertArticleOperation.INSERT_ON_RAIL_MESSAGE);
    } else {
      return this.insertByNeighbor(lastConnectedContainer.articleGUID, this._processingInsertData.rotationDelta,
        RelativePositionReferenceArticleLocationVO.RELATIVE_POSITION_RIGHT_OF, InsertArticleOperation.INSERT_ON_RAIL_MESSAGE);
    }
  }

  private insertArticleByNeighbors(): Promise<InsertedArticleData[] | null> {
    const neighborUsageIds: string[] = this.getSortedNeighborUsageIds(this._processingInsertData.neighborArticleUsageIds);
    const neighborRelativeArticleLocations: RelativePositionReferenceArticleLocationVO[] =
      neighborUsageIds.flatMap((neighborId) => this.createArticleLocationsByOpenSide(neighborId, this._processingInsertData.rotationDelta));
    return this.insertArticleByNextNeighbors(neighborRelativeArticleLocations);
  }

  private async insertArticleByNextNeighbors(neighborRelativeArticleLocations: RelativePositionReferenceArticleLocationVO[]): Promise<InsertedArticleData[] | null> {
    const insertData: InsertedArticleData[] = [];
    for (const neighbor of neighborRelativeArticleLocations) {
      insertData.push(await this.insertByNeighborLocation(neighbor, InsertArticleOperation.INSERT_BY_NEIGHBOR_MESSAGE));
    }
    return insertData;
  }

  private createArticleLocationsByOpenSide(neighborUsageId: string, rotationDelta: number): RelativePositionReferenceArticleLocationVO[] {
    const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(neighborUsageId);
    const leftSideOpen: boolean = ArticleContainerUtils.isLeftSideOpen(articleContainer);
    const rightSideOpen: boolean = ArticleContainerUtils.isRightSideOpen(articleContainer);

    let articleRotation: number = ArticleUsageUtils.getArticleUsagePlausiPropertyIntValue(
      articleContainer.articleUsage,
      ArticleUsagePlausiPropertyDef.APPLIED_ROTATION_ANGLE
    );
    if (NumberUtils.isNotNumber(articleRotation)) {
      articleRotation = rotationDelta;
    } else if (NumberUtils.isNumber(rotationDelta)) {
      articleRotation += rotationDelta;
    }
    const result: RelativePositionReferenceArticleLocationVO[] = [];
    if (leftSideOpen || rightSideOpen) {
      if (leftSideOpen) {
        result.push(this.createNeighborArticleLocation(neighborUsageId, articleRotation, RelativePositionReferenceArticleLocationVO.RELATIVE_POSITION_LEFT_OF));
      }
      if (rightSideOpen) {
        result.push(this.createNeighborArticleLocation(neighborUsageId, articleRotation, RelativePositionReferenceArticleLocationVO.RELATIVE_POSITION_RIGHT_OF));
      }
    } else {
      result.push(this.createNeighborArticleLocation(neighborUsageId, articleRotation, RelativePositionReferenceArticleLocationVO.RELATIVE_POSITION_RIGHT_OF));
    }
    return result;
  }

  private replaceByUsageId(usageId: string, notificationMessage: string): Promise<InsertedArticleData> {
    const operation = new ReplaceReferenceArticleLocationVO();
    operation.articleUsageIdToReplace = usageId;
    operation.detachReplacedArticle = false;
    this._usageIdsToReplace.splice(this._usageIdsToReplace.indexOf(usageId), 1);
    this._replacedUsageIds.push(usageId);
    return this._insertArticleService.insertArticleByArticleLocation(this._articleToCreate, operation, notificationMessage);
  }

  private insertByOffset(railUsageId: string, offset: number, rotationAngle: number, notificationMessage: string): Promise<InsertedArticleData> {
    const onRailLocation: OnRailReferenceArticleLocationVO = new OnRailReferenceArticleLocationVO();
    onRailLocation.rangeOnLineArticleUsage = this.projectModel.getArticleUsage(railUsageId);
    onRailLocation.articleOffset = offset;
    onRailLocation.rotationAngle = rotationAngle;
    return this._insertArticleService.insertArticleByArticleLocation(this._articleToCreate, onRailLocation, notificationMessage);
  }

  private insertByNeighbor(neighborUsageId: string, rotation: number, relativePosition: number, notificationMessage: string): Promise<InsertedArticleData> {
    const neighborArticleLocation: RelativePositionReferenceArticleLocationVO = this.createNeighborArticleLocation(neighborUsageId,
      rotation, relativePosition);
    return this.insertByNeighborLocation(neighborArticleLocation, notificationMessage);
  }

  private insertByNeighborLocation(neighborArticleLocation: RelativePositionReferenceArticleLocationVO,
    notificationMessage: string): Promise<InsertedArticleData> {
    return this._insertArticleService.insertArticleByArticleLocation(this._articleToCreate, neighborArticleLocation, notificationMessage);
  }

  private createNeighborArticleLocation(
    neighborUsageId: string,
    rotation: number,
    relativePosition: number
  ): RelativePositionReferenceArticleLocationVO {
    const neighborArticleLocation: RelativePositionReferenceArticleLocationVO = new RelativePositionReferenceArticleLocationVO();
    neighborArticleLocation.referenceArticleUsage = this.projectModel.getArticleUsage(neighborUsageId);
    neighborArticleLocation.relativePosition = relativePosition;
    if (NumberUtils.isNumber(rotation)) {
      neighborArticleLocation.rotationAngle = rotation;
    }
    return neighborArticleLocation;
  }

  private getSortedNeighborUsageIds(neighborUsageIds: string[]): string[] {
    if (!neighborUsageIds) {
      return null;
    }
    const neighborContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(neighborUsageIds);
    const sortedContainers: ArticleContainer[] = this._insertHelper.sortArticles(neighborContainers);
    return ArticleContainerUtils.extractArticleGUIDs(sortedContainers);
  }

  private mergeMoveArticlesMap(moveMap: Dictionary<string, Vector3>): void {
    if (!moveMap) {
      return;
    }
    if (!this._moveArticlesMap) {
      this._moveArticlesMap = new Dictionary<string, Vector3>();
    }
    InsertArticleUtils.mergeMoveMap(this._moveArticlesMap, moveMap);
  }

  private storeInsertResult(
    addedArticleGUIDs: string[],
    moveMap: Dictionary<string, Vector3>,
    createUndoOperation: boolean,
    additionalOperations: IOperation[]
  ): void {
    if (!addedArticleGUIDs) {
      return;
    }
    if (createUndoOperation) {
      this._createdContainers = this._createdContainers.concat(this.containerService.findArticleContainersInGlobalByGuids(addedArticleGUIDs));
      this.mergeMoveArticlesMap(moveMap);
    }
    if (additionalOperations && additionalOperations.length > 0) {
      this._createdUndoOperations = this._createdUndoOperations.concat(additionalOperations);
    }
  }

  private articleOnRailInserted(data: InsertedArticleData): void {
    const addedArticleGUIDs: string[] = data.insertedArticleGUIDs;
    const moveMap: Dictionary<string, Vector3> = data.moveArticlesMap;
    const createUndoOperation: boolean = data.createUndoOperation;
    const additionalUndoOperations: IOperation[] = data.undoOperations;
    this.storeInsertResult(addedArticleGUIDs, moveMap, createUndoOperation, additionalUndoOperations);
    this._articleInserted = true;
  }

  private async articleByNeighborsInserted(datas: InsertedArticleData[]): Promise<void> {
    for (const data of datas) {
      const addedArticleGUIDs: string[] = data.insertedArticleGUIDs;
      const moveMap: Dictionary<string, Vector3> = data.moveArticlesMap;
      const createUndoOperation: boolean = data.createUndoOperation;
      const additionalUndoOperations: IOperation[] = data.undoOperations;
      this.storeInsertResult(addedArticleGUIDs, moveMap, createUndoOperation, additionalUndoOperations);
      this._articleInserted = true;
    }
  }

  private insertAccessoryOnRail_failure(message: string): void {
    if (!this._failureMessage) {
      this._failureMessage = message;
    }
  }

  private insertAccessoryByNeighbor_failure(message: string): void {
    if (!this._failureMessage) {
      this._failureMessage = message;
    }
  }

  private async finalizeInsertOperation(): Promise<{removeSelection: boolean}> {
    if (this._articleInserted) {
      return this.finalizeInsertSuccessOperation();
    } else {
      throw new Error(this._failureMessage);
    }
  }

  private finalizeInsertSuccessOperation(): {removeSelection: boolean} {
    let removeSelection: boolean = true;
    if (!this._replacedUsageIds.length) {
      this.postProcessCreatedArticles();
      removeSelection = false;
    }
    return {removeSelection}
  }

  private postProcessCreatedArticles(): void {
    this.openLabelEditorHelper.showLabelEditorForCoverLabelArticles(this._createdContainers);
  }

  public clear(): void {
    this._insertHelper = null;
    this._articleToCreate = null;
    this._processingInsertData = null;
    this._createdContainers = [];
    this._createdUndoOperations = [];

    this._articleInserted = false;
    this._failureMessage = null;

    this._usageIdsToReplace = [];
    this._replacedUsageIds = [];
  }
}
